<template>
    <div
        class="z-20 p-4 py-2"
        :class="transparent ? 'absolute top-0 left-0 right-0 bg-transparent' : 'bg-bg sticky top-0'"
    >
        <div class="relative flex items-center justify-between py-3">
            <button
                v-if="showBackButton"
                @click="handleBackClick"
                data-testid="page-back-button"
                class="absolute left-0 z-10 flex items-center"
            >
                <template v-if="$route.name === 'home'">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M9.3051 6.28027L10.6934 7.71996L8.47696 9.85722L18.9992 9.85703V15.9999H16.9992V11.8571L8.47687 11.8572L10.6934 13.9946L9.3051 15.4342L4.55859 10.8573L9.3051 6.28027Z"
                            fill="white"
                        />
                    </svg>
                    <span class="pl-1 text-xs font-bold">{{ $t('page_header.back_to_shop') }}</span>
                </template>

                <svg
                    v-else
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M15.707 6.70703L10.4141 12L15.707 17.293L14.293 18.707L7.58594 12L14.293 5.29297L15.707 6.70703Z"
                        fill="white"
                    />
                </svg>
            </button>

            <span
                v-if="title && !$slots.logo"
                class="section-title absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-8 text-center whitespace-nowrap"
            >
                {{ title }}
            </span>

            <div
                v-if="$slots.logo"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
                <slot name="logo"></slot>
            </div>

            <div
                v-if="$slots['right-content']"
                class="absolute right-0 flex items-center gap-3"
            >
                <slot name="right-content"></slot>
            </div>
        </div>

        <modal
            :is-visible="showCloseModal"
            :on-confirm="handleBackClick"
            :on-decline="
                () => {
                    showCloseModal = false
                }
            "
            :on-extra="isInMatch && canPauseCurrentGame ? handlePauseClick : null"
            :extra-text="$t('page_header.pause')"
            :confirm-text="isInMatch ? $t('page_header.leave') : null"
            :is-loading="isLoading"
        >
            <template v-if="!isInMatch">
                <h2
                    class="justify-start text-center font-['Termina'] text-base leading-normal font-extrabold text-white"
                >
                    {{ $t('page_header.leave_gameon_title') }}
                </h2>
                <p
                    class="mt-4 justify-start self-stretch text-center font-sans text-sm leading-normal font-normal text-white"
                >
                    {{ $t('page_header.leave_gameon_body') }}
                </p>
            </template>
            <template v-else>
                <h2
                    class="justify-start text-center font-['Termina'] text-base leading-normal font-extrabold text-white"
                >
                    {{ $t('page_header.leave_match_title') }}
                </h2>
                <p
                    class="mt-4 justify-start self-stretch text-center font-sans text-sm leading-normal font-normal text-white"
                >
                    {{
                        canPauseCurrentGame
                            ? $t('page_header.leave_match_pause_body')
                            : $t('page_header.leave_match_body')
                    }}
                </p>
            </template>
        </modal>
    </div>
</template>

<script>
import Modal from '@/components/Modal.vue'
import { useMatchStore } from '@/stores/match.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import x01Game from '@/services/games/1_x01'
import cricketGame from '@/services/games/2_cricket'
import killerGame from '@/services/games/4_killer'
import camelraceGame from '@/services/games/3_camelrace'
import segmentsTrainingGame from '@/services/games/5_segments'

// Matchtype ID -> game service, so the exit modal can check `canPause` for
// whichever game is currently in progress. Mirrors the map in LobbyView.vue.
const GAME_SERVICES = {
    1: x01Game,
    3: cricketGame,
    4: killerGame,
    7: camelraceGame,
    8: segmentsTrainingGame,
}

export default {
    name: 'PageHeader',
    components: {
        Modal,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        backRoute: {
            type: [String, Object],
            default: null,
        },
        showBackButton: {
            type: Boolean,
            default: true,
        },
        transparent: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showCloseModal: false,
            isLoading: false,
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        isInMatch() {
            return !!this.matchStore.match.id && !this.matchStore.isMatchOver
        },
        canPauseCurrentGame() {
            return !!GAME_SERVICES[this.matchStore.match.matchtype_id]?.canPause
        },
    },
    emits: ['back'],
    methods: {
        // Pausing never calls the backend: it just stops this client from
        // syncing and goes home, leaving the match row exactly as it was
        // (matchend_at stays null) so it resumes later via the "Ongoing"
        // entry on the home screen, same as backgrounding the tab does today.
        handlePauseClick() {
            this.matchStore.clearMatch()
            this.showCloseModal = false
            this.$router.push({ name: 'home' })
        },
        async handleBackClick() {
            // Only show confirmation for home and match cases
            if (!this.showCloseModal && (this.$route.name === 'home' || this.isInMatch)) {
                this.showCloseModal = true
                return
            }

            // For all other cases, just navigate back
            if (!this.$route.name === 'home' && !this.isInMatch) {
                if (this.backRoute) {
                    if (typeof this.backRoute === 'string') {
                        this.$router.push({ name: this.backRoute })
                    } else {
                        this.$router.push(this.backRoute)
                    }
                } else {
                    this.$emit('back')
                }
                return
            }

            // Handle home and match cases with confirmation
            this.isLoading = true

            try {
                // If we're in a match, cancel it and redirect to home
                if (this.isInMatch) {
                    await this.matchStore.cancelMatch(this.matchStore.match.id)
                    this.matchStore.clearMatch()
                    this.$router.push({ name: 'home' })
                    return
                }

                // Handle navigation for non-match cases
                if (this.$route.name === 'home') {
                    // Leaving Game On for the shop app. The screen it was left from comes along
                    // on the envelope, so this says where people stop playing.
                    this.$analytics.trackEvent(ANALYTICS_EVENTS.GAMEON_CLOSED, {
                        reason: 'user_action',
                    })
                    this.$bridge.emit('gameon.close', { reason: 'user_action' })
                } else if (this.backRoute) {
                    if (typeof this.backRoute === 'string') {
                        this.$router.push({ name: this.backRoute })
                    } else {
                        this.$router.push(this.backRoute)
                    }
                } else {
                    this.$emit('back')
                }
            } catch (error) {
                console.error('Failed to leave/cancel:', error)
                this.$toast(this.$t('general.something_went_wrong'), 5000, this.$t('page_header.error_leave_game'))
            } finally {
                this.isLoading = false
                this.showCloseModal = false
            }
        },
    },
}
</script>
